import { createFileRoute, Link } from '@tanstack/react-router'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import {
  adminCreateRateException,
  adminListRateExceptions,
  adminRevokeRateException,
} from '@/gen/cortado/v1/admin-AdminService_connectquery'
import type { RateException } from '@/gen/cortado/v1/admin_pb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DateInput } from '@/components/ui/date-input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { OrganizationSelect } from '@/components/billing/organization-select'
import { dateInputToTimestamp, formatDate, formatUsdCents, type BadgeVariant } from '@/lib/billing'

export const Route = createFileRoute('/_admin/billing/exceptions')({
  component: RateExceptionsPage,
})

type KindFilter = 'all' | 'rate' | 'trial'
type StatusFilter = 'all' | 'PENDING' | 'APPLIED' | 'EXPIRED' | 'REVOKED' | 'FAILED'

const STATUS_BADGE: Record<string, BadgeVariant> = {
  PENDING: 'warning',
  APPLIED: 'success',
  EXPIRED: 'secondary',
  REVOKED: 'danger',
  FAILED: 'danger',
}

const exceptionsListKey = createConnectQueryKey({
  schema: adminListRateExceptions,
  cardinality: undefined,
})

// Rejects anything with more than 2 decimal places so a typo like "50.005"
// can't silently round away a cent instead of failing loudly.
const DOLLARS_INPUT_PATTERN = /^\d+(\.\d{1,2})?$/

function parseDollarsToCents(value: string): number | null {
  if (!DOLLARS_INPUT_PATTERN.test(value.trim())) return null
  const cents = Math.round(parseFloat(value) * 100)
  return Number.isFinite(cents) ? cents : null
}

function RateExceptionsPage() {
  const queryClient = useQueryClient()
  const [kindFilter, setKindFilter] = useState<KindFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [revokeTarget, setRevokeTarget] = useState<RateException | null>(null)

  const { data, isLoading, isError, error } = useQuery(adminListRateExceptions, {
    status: statusFilter === 'all' ? undefined : statusFilter,
    exceptionKind: kindFilter === 'all' ? undefined : kindFilter,
  })

  const rows = data?.rateExceptions ?? []

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: exceptionsListKey })

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Rate exceptions</h1>
        <p className="text-muted-foreground text-sm">
          The R-80/§29 approval ledger — pricing exceptions and trial extensions (trial rows are
          created from the "Set billing start date" dialog on an org's billing page).
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <CreateRateExceptionCard onSuccess={invalidateList} />

        <Card>
          <CardHeader>
            <CardTitle>Exception ledger</CardTitle>
            <CardDescription>Rate exceptions and trial extensions on file.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as KindFilter)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All kinds</SelectItem>
                  <SelectItem value="rate">Rate</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPLIED">Applied</SelectItem>
                  <SelectItem value="EXPIRED">Expired</SelectItem>
                  <SelectItem value="REVOKED">Revoked</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                {error instanceof Error ? error.message : 'Unable to load rate exceptions'}
              </div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>Amount / days</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Effective</TableHead>
                      <TableHead>Approver</TableHead>
                      <TableHead className="w-20 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : !rows.length ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No exceptions on file
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Link
                              to="/billing/$organizationId"
                              params={{ organizationId: row.organizationId }}
                              className="font-medium hover:underline"
                            >
                              {row.accountName}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Badge variant={row.exceptionKind === 'trial' ? 'info' : 'purple'}>
                              {row.exceptionKind}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {row.exceptionKind === 'trial'
                              ? `${row.approvedTrialDays ?? '—'}d`
                              : row.amountCents !== undefined
                                ? `${formatUsdCents(row.amountCents)}/mo`
                                : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_BADGE[row.status] ?? 'secondary'}>{row.status}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{formatDate(row.effectiveDate)}</TableCell>
                          <TableCell className="text-muted-foreground">{row.approverName}</TableCell>
                          <TableCell className="text-right">
                            {row.status === 'APPLIED' || row.status === 'PENDING' ? (
                              <Button variant="outline" size="sm" onClick={() => setRevokeTarget(row)}>
                                Revoke
                              </Button>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {revokeTarget && (
        <RevokeExceptionDialog
          exception={revokeTarget}
          open
          onOpenChange={(open) => !open && setRevokeTarget(null)}
          onSuccess={invalidateList}
        />
      )}
    </div>
  )
}

function CreateRateExceptionCard({ onSuccess }: { onSuccess: () => void }) {
  const [organizationId, setOrganizationId] = useState('')
  const [accountName, setAccountName] = useState('')
  const [approvedRateDescription, setApprovedRateDescription] = useState('')
  const [reason, setReason] = useState('')
  const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [expirationDate, setExpirationDate] = useState('')
  const [approverName, setApproverName] = useState('')
  const [approvalReference, setApprovalReference] = useState('')
  const [amountDollars, setAmountDollars] = useState('')
  const [seatCoverageDollars, setSeatCoverageDollars] = useState('')

  const createMutation = useMutation(adminCreateRateException, {
    onSuccess: () => {
      toast.success('Rate exception recorded')
      setOrganizationId('')
      setAccountName('')
      setApprovedRateDescription('')
      setReason('')
      setExpirationDate('')
      setApproverName('')
      setApprovalReference('')
      setAmountDollars('')
      setSeatCoverageDollars('')
      onSuccess()
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to record rate exception')
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const effective = dateInputToTimestamp(effectiveDate)
    if (!organizationId || !accountName.trim() || !approvedRateDescription.trim() || !reason.trim()
      || !effective || !approverName.trim() || !approvalReference.trim() || !amountDollars.trim()) {
      toast.error('All six approval fields and a dollar amount are required (R-80/§29)')
      return
    }
    const amountCents = parseDollarsToCents(amountDollars)
    if (amountCents === null || amountCents <= 0) {
      toast.error('Enter a dollar amount with at most 2 decimal places')
      return
    }

    let seatCoverageCents: number | null = null
    if (seatCoverageDollars.trim()) {
      seatCoverageCents = parseDollarsToCents(seatCoverageDollars)
      if (seatCoverageCents === null || seatCoverageCents < 0) {
        toast.error('Enter a valid seat coverage amount with at most 2 decimal places')
        return
      }
    }

    createMutation.mutate({
      organizationId,
      accountName: accountName.trim(),
      approvedRateDescription: approvedRateDescription.trim(),
      reason: reason.trim(),
      effectiveDate: effective,
      expirationDate: dateInputToTimestamp(expirationDate),
      approverName: approverName.trim(),
      approvalReference: approvalReference.trim(),
      amountCents: BigInt(amountCents),
      ...(seatCoverageCents !== null ? { seatCoverageCents: BigInt(seatCoverageCents) } : {}),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>New rate exception</CardTitle>
        <CardDescription>
          Every field is required by R-80/§29 — no undocumented one-off pricing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label>Organization</Label>
            <OrganizationSelect
              value={organizationId}
              onValueChange={(id, name) => {
                setOrganizationId(id)
                if (!accountName) setAccountName(name)
              }}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exception-account-name">Account name</Label>
            <Input
              id="exception-account-name"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exception-rate-description">Approved rate</Label>
            <Input
              id="exception-rate-description"
              placeholder="e.g. $50/mo off base price"
              value={approvedRateDescription}
              onChange={(e) => setApprovedRateDescription(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exception-amount">Amount off per month (dollars)</Label>
            <Input
              id="exception-amount"
              type="number"
              min="0.01"
              step="0.01"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              required
            />
            <p className="text-muted-foreground text-xs">
              This is a <strong>per-month</strong> amount — Cortado multiplies it by each product's
              term length before creating the Stripe Billing discount (e.g. $50/mo on an annual customer
              creates a $600.00 discount, not $50.00). Fixed dollar amount only — no percentage
              input. A percentage discount can't be scoped to the base subscription price alone in
              Stripe Billing and would silently discount seat pricing too (R-106). Attaching this to a live
              subscription applies it starting the next billing cycle, not immediately.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exception-seat-coverage">Seat coverage per month (dollars, optional)</Label>
            <Input
              id="exception-seat-coverage"
              type="number"
              min="0"
              step="0.01"
              value={seatCoverageDollars}
              onChange={(e) => setSeatCoverageDollars(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              Rate exceptions don't discount seat pricing by default (R-106). Set this only when the
              approved rate specifically also covers additional-seat charges — like the amount above,
              it's a per-month figure multiplied by each product's term length.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exception-reason">Reason</Label>
            <Textarea
              id="exception-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="exception-effective-date">Effective date</Label>
              <DateInput
                id="exception-effective-date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="exception-expiration-date">Expiration (optional)</Label>
              <DateInput
                id="exception-expiration-date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="exception-approver">Approver (Jonny)</Label>
              <Input
                id="exception-approver"
                value={approverName}
                onChange={(e) => setApproverName(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="exception-approval-reference">Approval reference</Label>
              <Input
                id="exception-approval-reference"
                placeholder="Link or note evidencing written approval"
                value={approvalReference}
                onChange={(e) => setApprovalReference(e.target.value)}
                required
              />
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Record exception
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function RevokeExceptionDialog({
  exception,
  open,
  onOpenChange,
  onSuccess,
}: {
  exception: RateException
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}) {
  const [reason, setReason] = useState('')
  const mutation = useMutation(adminRevokeRateException, {
    onSuccess: () => {
      toast.success('Exception revoked')
      onSuccess()
      onOpenChange(false)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to revoke exception')
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke exception for {exception.accountName}?</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="revoke-exception-reason">Reason</Label>
            <Textarea
              id="revoke-exception-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!reason.trim() || mutation.isPending}
              onClick={() => mutation.mutate({ id: exception.id, reason: reason.trim() })}
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Revoke
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
